// cuentas.js — el trato con el servidor de cuentas.
//
// Contrato: `CUENTAS_WEB_INSTRUCCIONES.md` (31.08.26, backend en producción).
// Base: `https://listen.astra.fm/cuentas`. JSON, sin cookies, sesión por
// **Bearer token** en `Authorization`.
//
// Esto no guarda estado ni decide nada de la interfaz: habla con el servidor y
// devuelve lo que dice. La sesión vive en `stores/cuenta.js`.
//
// ── 🔴 Ninguna función lanza ──
//
// Todas devuelven `{ ok, ... }` o `{ ok: false, error }` con un mensaje que se
// puede enseñar tal cual. El contrato ya manda los errores así —`{ok:false,
// error:"mensaje legible"}`— y con red caída se inventa uno equivalente. Una
// pantalla de acceso que revienta con una excepción deja al visitante sin saber
// si el problema es su contraseña o el wifi.
//
// ── El proxy de desarrollo ──
//
// ⚠️ El backend refleja `astra.fm` y `www.astra.fm`, y a `localhost` no le manda
// `Access-Control-Allow-Origin`. Comprobado el 31.08.26. Así que en desarrollo
// las llamadas salen por el proxy del dev server —ruta relativa— y en producción
// van al dominio del servidor. Es lo mismo que ya se hace con AzuraCast.
import { LISTEN } from "./host";
import { i18n } from "@/i18n";
import { mensajeDeError } from "@/i18n/errores";
import { IDIOMA, enIdioma } from "@/i18n/idioma";

const BASE =
  process.env.NODE_ENV === "development"
    ? "/cuentas"
    : `${LISTEN}/cuentas`;

/** Un error con la misma forma que los del servidor, para no distinguirlos. */
function falla(error) {
  return { ok: false, error };
}

/**
 * La llamada, con **un solo reintento** si no llega a haber respuesta.
 *
 * 🔴 Encargo de Pablo, 24.09.26, con el desglose del servidor delante: de los
 * fallos del alta medidos, **ninguno llegó al servidor**. Un segundo intento
 * rescata los que se caen por un tropiezo de red.
 *
 * ⚠️ **Solo cuando `fetch` lanza**, es decir, cuando no hubo respuesta de
 * ninguna clase. Si el servidor contesta —aunque sea un 500— no se repite: eso
 * ya es una respuesta y repetirla sería pedir dos veces lo mismo.
 *
 * ⚠️ **Y sí, se repite un `POST`.** No es descuido: si no hubo respuesta, casi
 * siempre es que no llegó. Y en el caso raro de que llegara y se perdiera la
 * respuesta, el contrato protege: dar de alta dos veces el mismo correo
 * devuelve `409` y pedir dos veces un perfil también, así que lo peor que pasa
 * es un mensaje claro con salida, no una cuenta duplicada.
 *
 * El segundo va **1,2 s después**: lo justo para que un cambio de antena o un
 * DNS lento se resuelvan, sin que se note como una espera.
 */
async function unIntento(ruta, metodo, cabeceras, cuerpo) {
  const llamada = () =>
    fetch(BASE + ruta, {
      method: metodo,
      headers: cabeceras,
      body: cuerpo ? JSON.stringify(cuerpo) : undefined,
    });

  try {
    return await llamada();
  } catch (error) {
    await new Promise((sigue) => setTimeout(sigue, 1200));
    return llamada();
  }
}

/**
 * Una llamada al servidor de cuentas.
 *
 * @param {string} ruta      `/login`, `/me`…
 * @param {object} opciones  `metodo`, `cuerpo`, `token`
 */
async function pide(ruta, { metodo = "GET", cuerpo = null, token = "" } = {}) {
  const cabeceras = { Accept: "application/json" };

  // 🔴 **`text/plain` y no `application/json`, para que no haya preflight.**
  //
  // Un `POST` con `Content-Type: application/json` obliga al navegador a
  // preguntar antes con un `OPTIONS`, y **el preflight es justo lo que peor
  // llevan los navegadores incrustados en otras apps** —Instagram, Android
  // WebView—, que es de donde salen 44 de los 47 fallos del alta que midió el
  // servidor el 24.09.26. Con `text/plain` la petición pasa a ser *simple*: un
  // solo viaje.
  //
  // El servidor acepta las dos formas desde el 24.09.26 y el cuerpo es el mismo
  // JSON; solo cambia la etiqueta. Contrato `CUENTAS_WEB_INSTRUCCIONES.md` §2.1.
  //
  // ⚠️ **Con token sigue habiendo preflight** y no hay manera: `Authorization`
  // no está en la lista blanca de CORS. Da igual, porque lo que falla es el
  // alta, y el alta no lleva token.
  if (cuerpo) cabeceras["Content-Type"] = "text/plain";
  // Sin token no se manda la cabecera: un `Bearer ` vacío es peor que nada,
  // porque el servidor tiene que decidir qué hacer con una sesión a medias.
  if (token) cabeceras.Authorization = `Bearer ${token}`;

  let r;
  try {
    r = await unIntento(ruta, metodo, cabeceras, cuerpo);
  } catch (error) {
    // Aquí no se ha llegado ni a hablar con el servidor, **dos veces**: red
    // caída, DNS, CORS, o algo en el navegador de quien lo intenta.
    //
    // 🔴 **Y queda escrito en la consola.** Lo pidió el servidor el 20.09.26
    // (`ALTAS_CON_ERROR_WEB.md` §2): veían 36 altas fallidas con `motivo: otro`
    // y ni rastro en su lado, así que no se podía saber si era su respuesta o
    // que la petición no llegaba. Sin identificar a nadie: la ruta y el fallo.
    // ⚠️ `console.error` y no `warn`: `main.js` silencia `log`, `debug` y `warn`
    // en toda la web, así que un aviso por ahí no se vería justo donde hace
    // falta, que es el navegador de quien no consigue darse de alta.
    console.error(`⚠️ cuentas: la petición a ${ruta} no llegó a responder ·`, error?.message || error);

    // 🔴 **El nombre del fallo viaja.** Lo pidió el servidor el 24.09.26: con
    // solo «no llegó» tardamos tres idas y venidas en saber por qué. `TypeError`
    // es red o CORS, `AbortError` es que se fueron de la página, `NotAllowedError`
    // es un permiso denegado. Son nombres de clase, no dicen nada de nadie.

    // 🔴 **El aviso ya no manda a mirar la conexión.** Medido el 24.09.26 con el
    // desglose del servidor: **9 de 9** de los fallos «otro» no llegaron nunca a
    // su lado, y desde 19 navegadores salieron 47 intentos —dos y medio por
    // persona—. O sea que reintentaron y siguió fallando: no es un bache de red,
    // y a quien tiene la conexión perfecta este mensaje lo mandaba a mirar donde
    // no era. El alta pide a **otro dominio**, `listen.astra.fm`, que es justo
    // lo que cortan los bloqueadores y los filtros de red.
    return {
      ...falla(i18n.global.t("errores.sinConexion")),
      sinRespuesta: true,
      fallo: error?.name || "Error",
    };
  }

  let datos = null;
  try {
    datos = await r.json();
  } catch (error) {
    datos = null;
  }

  if (!r.ok) {
    // Los códigos que el contrato prevé se tratan arriba y no hacen ruido; lo
    // que no encaja se apunta con su código y su cuerpo, que es justo lo que
    // el servidor nos pidió poder mirar.
    if (![400, 401, 403, 409, 429].includes(r.status)) {
      console.error(`⚠️ cuentas: ${metodo} ${ruta} respondió ${r.status} ·`, datos || "(sin cuerpo JSON)");
    }
    return {
      ok: false,
      // El mensaje sale del `codigo` del servidor, en el idioma de la página
      // (`i18n/errores.js`). Si el código no lo conocemos, su texto en español.
      error: mensajeDeError(datos, r.status),
      codigo: datos?.codigo || "",
      campos: Array.isArray(datos?.campos) ? datos.campos : [],
      // El código se conserva porque hay dos que la interfaz trata distinto:
      // `401` es «sesión fuera» y `409` es «ya existe».
      estado: r.status,
      // 🔴 Las cuentas migradas de la app antigua no tienen contraseña propia.
      // Desde el 11.09.26 ya no se validan contra WordPress: el login responde
      // `401` con `debeRestablecer: true`, y la pantalla ofrece restablecer en
      // vez de dejar a la persona probando contraseñas. Contrato de cuentas,
      // cabecera del 12.09.26. Aditivo: en los demás fallos viene `false`.
      debeRestablecer: datos?.debeRestablecer === true,
      // 🔴 Y desde el 13.09.26 la verificación del correo es obligatoria: sin
      // ella, editar datos, cambiar la contraseña y pedir perfil responden `403`
      // con `debeVerificar: true`. La interfaz decide por `emailVerificado` y no
      // espera a este error, pero si llega, recarga la cuenta y enseña el aviso.
      debeVerificar: datos?.debeVerificar === true,
    };
  }

  return { ok: true, ...(datos || {}) };
}

// ── Sin sesión ──

/**
 * De qué teléfono viene el alta, para el campo `dispositivo`.
 *
 * 🔴 **No es telemetría: decide por dónde se puede invitar a la beta.**
 * TestFlight es solo de Apple, así que quien pide probar la app desde un
 * Android va por la prueba de Google Play, que es otro camino. Sin este dato el
 * correo queda en la lista sin saber por dónde invitarlo y hay que preguntar
 * después. (`BETA_APP_WEB_INSTRUCCIONES.md` § 2, leído del portal el 09.09.26.)
 *
 * El contrato acepta cualquier cosa legible y deduce la plataforma, así que no
 * hace falta el modelo: basta el sistema. Devuelve `undefined` cuando no se
 * puede saber, y entonces el campo no se manda — el servidor lo deja en `null`.
 */
function dispositivo() {
  if (typeof navigator === "undefined") return undefined;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPod/.test(ua)) return "iphone";
  if (/iPad/.test(ua)) return "ipad";
  // iPadOS 13+ se declara Macintosh y solo se delata por el táctil.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ipad";
  if (/Android/.test(ua)) return "android";
  return undefined;
}

/**
 * Alta. El contrato pide lo mínimo: correo, contraseña de 8 o más y los tres
 * consentimientos. `nombre` y `perfil` son opcionales.
 *
 * `beta` es la casilla «quiero probar la app antes», aditiva desde el 09.09.26:
 * si no se marca **no se manda el campo**, y el alta se comporta como siempre.
 * Nunca se manda `null`, que es lo único que el contrato prohíbe.
 *
 * `409` significa que ese correo ya está registrado.
 */
export function registrar({ email, password, consents, nombre, perfil, beta }) {
  return pide("/register", {
    metodo: "POST",
    cuerpo: {
      email,
      password,
      consents,
      nombre,
      perfil,
      // El idioma de la página (04.11.26). Por ahora el servidor solo lo guarda.
      idioma: IDIOMA,
      ...(beta ? { beta: true, dispositivo: dispositivo() } : {}),
    },
  });
}

/**
 * El alta por formulario, para cuando `fetch` no llega.
 *
 * 🔴 **Es una navegación, no una petición.** Y por eso funciona donde lo otro
 * no: un envío de formulario no pasa por `fetch`, ni por CORS, ni lo intercepta
 * el service worker. Es el suelo que aguanta en los navegadores incrustados en
 * otras apps —Instagram, Android WebView—, de donde salen 44 de los 47 fallos
 * del alta que midió el servidor el 24.09.26.
 *
 * Lo propuso el servidor en `CUENTAS_WEB_INSTRUCCIONES.md` §2.1: «en vez de
 * reintentar el mismo `fetch`, enviad el formulario. Es un reintento que sí
 * cambia algo».
 *
 * ⚠️ **Los campos van planos**, porque un formulario no manda objetos: los tres
 * consentimientos sueltos y el perfil como `perfil_tipo` y `perfil_nombre`.
 *
 * ⚠️ **No devuelve token**, a propósito del servidor: acabaría en el historial
 * del navegador y en el `Referer`. Se vuelve con `?alta=ok` y la cuenta creada,
 * y a esa persona hay que pedirle que entre con normalidad.
 */
export function mandaElFormularioDeAlta({ email, password, consents, nombre, perfil, beta }) {
  if (typeof document === "undefined") return;

  const campos = {
    email,
    password,
    nombre,
    // La vuelta la fija la web; el servidor solo acepta astra.fm y www.
    volver: `${window.location.origin}${enIdioma("/cuenta", IDIOMA)}`,
    // Un consentimiento que no se marca no se manda, como haría un checkbox.
    ...(consents?.novedades ? { novedades: "1" } : {}),
    ...(consents?.noticias ? { noticias: "1" } : {}),
    ...(consents?.astraUpdates ? { astraUpdates: "1" } : {}),
    ...(perfil?.tipo ? { perfil_tipo: perfil.tipo, perfil_nombre: perfil.nombre } : {}),
    ...(beta ? { beta: "1", dispositivo: dispositivo() } : {}),
  };

  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${BASE}/register-form`;
  form.hidden = true;

  for (const [clave, valor] of Object.entries(campos)) {
    if (valor === undefined || valor === null || valor === "") continue;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = clave;
    input.value = String(valor);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

/** Acceso. Reactiva una cuenta en baja suave, así que también sirve de vuelta. */
export function entrar({ email, password }) {
  return pide("/login", { metodo: "POST", cuerpo: { email, password } });
}

/**
 * Pide el correo para restablecer la contraseña.
 *
 * 🔴 Responde `{ok:true}` SIEMPRE, exista el correo o no, y la interfaz tiene
 * que decir lo mismo en los dos casos: si distinguiera, cualquiera podría
 * averiguar quién tiene cuenta preguntando.
 */
export function pedirReset(email) {
  return pide("/request-reset", { metodo: "POST", cuerpo: { email } });
}

/** La contraseña nueva, desde el enlace del correo. Caduca en una hora. */
export function restablecer({ email, token, password }) {
  return pide("/reset-password", {
    metodo: "POST",
    cuerpo: { email, token, password },
  });
}

/**
 * Verifica el correo desde el enlace. **Sin Bearer**: lo llama la página del
 * enlace, donde puede que no haya sesión. Caduca en 24 horas.
 */
export function verificarCorreo({ email, token }) {
  return pide("/verify-email", { metodo: "POST", cuerpo: { email, token } });
}

// ── Con sesión ──

/** La cuenta. `401` significa sesión fuera. */
export function yo(token) {
  return pide("/me", { token });
}

/** Cambia nombre o consentimientos. Devuelve la cuenta entera. */
export function actualizar(token, { nombre, consents }) {
  return pide("/me", { metodo: "PUT", token, cuerpo: { nombre, consents } });
}

/**
 * Cambia la contraseña.
 *
 * ⚠️ Devuelve un token **NUEVO** y revoca los demás: hay que guardarlo o la
 * propia sesión que acaba de cambiarla se queda fuera en la siguiente llamada.
 */
export function cambiarContrasena(token, { actual, nueva }) {
  return pide("/password", { metodo: "POST", token, cuerpo: { actual, nueva } });
}

/**
 * Solicita perfil de artista o de sello.
 *
 * El estado que devuelve importa: con el correo verificado queda `pendiente` y
 * llega a la redacción —salvo el artista, que pasa antes por `sin-material`
 * hasta mandar algo que escuchar—; sin verificar queda `sin-verificar-email` y **la
 * solicitud se aparca** hasta que se verifique. La interfaz enseña ese estado,
 * nunca lo decide.
 */
export function solicitarPerfil(token, { tipo, nombre, web, cargo }) {
  return pide("/perfil", {
    metodo: "POST",
    token,
    cuerpo: { tipo, nombre, web, cargo },
  });
}

/** Reenvía el correo de verificación. `already:true` si ya estaba verificado. */
export function pedirVerificacion(token) {
  return pide("/request-verify", { metodo: "POST", token });
}

// ── El área que propone ──
//
// 🔴 **El área LEE y PROPONE; la redacción decide.** Nada de lo que el usuario
// escribe se publica en directo: cada cambio público entra en una cola con
// estados `pendiente · aplicada · rechazada`. La única excepción son los datos
// privados del sello —persona de contacto y cargo—, que se guardan al momento
// porque no los ve nadie.
//
// Contrato del 01.09.26, ampliación del de cuentas.

/**
 * Propone cambios de la ficha de artista. **Un solo lote por pantalla**: se
 * manda lo que el usuario tocó y sale como UNA propuesta.
 *
 * - `enlaces`: las seis claves fijas. Lo que no sea `https://…` se guarda como
 *   `null` —es decir, se quita—, y lo que no se manda no se toca.
 * - `conciertos`: **sustituye la lista entera**, así que van todas las filas.
 *   Cada una necesita fecha, sala y ciudad. Máximo 20.
 * - `foto` viaja como URL pero **la redacción no la aplica en automático**: la
 *   sube a mano. La pantalla lo dice.
 * - Un campo a `null` es una petición de vaciarlo.
 *
 * `409` es que el artista aún no tiene ficha; `403`, que no hay perfil
 * verificado. La respuesta ya trae la `cuenta` con la propuesta dentro, así que
 * no hace falta pedir `/me` otra vez.
 */
export function proponerFicha(token, cambios) {
  return pide("/ficha/proponer", { metodo: "POST", token, cuerpo: cambios });
}

/** Los datos privados del sello. **Se guardan directo**: no los ve nadie. */
export function guardarSello(token, { contacto, cargo }) {
  return pide("/sello", { metodo: "PUT", token, cuerpo: { contacto, cargo } });
}

/** Los datos públicos del sello: nombre, web y lugar. Van a revisión. */
export function proponerSello(token, { nombre, web, lugar }) {
  return pide("/sello/proponer", {
    metodo: "POST",
    token,
    cuerpo: { nombre, web, lugar },
  });
}

/**
 * Propone un artista para el roster. Los tres primeros son obligatorios a
 * propósito: `relacion` —«contrato discográfico», «management»…— es lo que la
 * redacción comprueba, y `enlace` es **identificativo**, para saber sin dudas
 * qué artista es. ⚠️ No es un enlace de escucha.
 *
 * Este mismo endpoint es la segunda puerta del formulario de Radar: cuando un
 * sello manda material de un artista que no está en su roster.
 *
 * `409` es que ya está: se detecta por nombre normalizado.
 */
export function proponerRoster(token, { nombre, relacion, enlace, notas }) {
  return pide("/roster/proponer", {
    metodo: "POST",
    token,
    cuerpo: { nombre, relacion, enlace, notas },
  });
}

/**
 * Baja suave: se puede recuperar entrando de nuevo durante **30 días**, que es
 * cuando corre la purga automática del servidor. Web y servidor dicen lo mismo
 * desde el 31.08.26; el contrato que circulaba con 90 era una copia vieja.
 */
export function borrarCuenta(token) {
  return pide("/me", { metodo: "DELETE", token });
}
